import { Link } from 'react-router-dom';

const ServicesSection = () => {
  const services = [
    {
      icon: 'ri-code-s-slash-line',
      title: 'Technology & Digital Services',
      headline: 'Transform Your Digital Infrastructure',
      description: 'Comprehensive IT advisory, cybersecurity, cloud solutions, and AI-driven automation to modernize operations and enhance security.',
      benefits: [
        'Reduce IT costs through optimized infrastructure',
        'Strengthen cybersecurity posture and compliance',
        'Accelerate digital transformation initiatives'
      ],
      link: '/services/technology',
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Healthcare Operations',
      headline: 'Optimize Clinical & Administrative Performance',
      description: 'Hospital operations consulting, accreditation readiness, supply chain redesign, and digital health transformation for healthcare systems.',
      benefits: [
        'Improve patient outcomes and operational efficiency',
        'Achieve accreditation and regulatory compliance',
        'Streamline supply chain and reduce costs'
      ],
      link: '/services/healthcare',
    },
    {
      icon: 'ri-truck-line',
      title: 'Logistics & Transportation',
      headline: 'Maximize Fleet Efficiency & Compliance',
      description: 'Fleet optimization, FMCSA/DOT compliance, route planning, and transportation technology integration for logistics operators.',
      benefits: [
        'Reduce fuel costs and improve route efficiency',
        'Ensure full FMCSA and DOT compliance',
        'Integrate modern fleet management technology'
      ],
      link: '/services/logistics',
    },
    {
      icon: 'ri-gas-station-line',
      title: 'Gasoline Retail & C-Stores',
      headline: 'Drive Profitability in Fuel Retail',
      description: 'Site feasibility, margin strategy, store merchandising, fuel supply contracts, and financing advisory for gas station operators.',
      benefits: [
        'Optimize fuel margins and pricing strategies',
        'Enhance store layout and merchandising',
        'Negotiate favorable supplier contracts'
      ],
      link: '/services/fuel-retail',
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Business & Capital Advisory',
      headline: 'Strengthen Financial Position & Growth',
      description: 'Business restructuring, capital planning, financial modeling, lender-ready documentation, and risk advisory for enterprises.',
      benefits: [
        'Secure capital and optimize debt structure',
        'Build lender-ready financial documentation',
        'Mitigate risk and improve financial resilience'
      ],
      link: '/services/capital-advisory',
    },
  ];

  return (
    <section className="bg-black py-32 border-t border-gray-900">
      <div className="max-w-[1600px] mx-auto px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <p 
            className="text-sm uppercase tracking-[0.3em] mb-6"
            style={{
              background: 'linear-gradient(90deg, #7A6A5A 0%, #C4B8A8 50%, #7A6A5A 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Our Capabilities
          </p>
          <h2 className="text-4xl lg:text-5xl font-light text-white mb-6">
            Five Strategic Divisions
          </h2>
          <p className="text-base text-gray-500 max-w-2xl mx-auto">
            Comprehensive consulting services designed to transform operations and drive sustainable growth across critical business verticals.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="group bg-charcoal-dark rounded-xl p-8 hover:bg-charcoal transition-all duration-400 border border-transparent hover:border-copper/30"
            >
              {/* Icon */}
              <div 
                className="w-14 h-14 rounded-lg flex items-center justify-center mb-6"
                style={{
                  background: 'linear-gradient(135deg, rgba(122, 106, 90, 0.3) 0%, rgba(168, 144, 120, 0.2) 100%)',
                  border: '1px solid rgba(168, 144, 120, 0.3)',
                }}
              >
                <i 
                  className={`${service.icon} text-2xl`}
                  style={{
                    background: 'linear-gradient(90deg, #A89078 0%, #D8CCC0 50%, #A89078 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                ></i>
              </div>

              {/* Title */}
              <h3 className="text-xl font-semibold text-white mb-2 leading-tight">
                {service.title}
              </h3>

              {/* Headline */}
              <h4 
                className="text-base font-medium mb-4 leading-tight"
                style={{
                  background: 'linear-gradient(90deg, #A89078 0%, #C4B8A8 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {service.headline}
              </h4>

              {/* Description */}
              <p className="text-sm text-gray-500 leading-relaxed mb-6">
                {service.description}
              </p>

              {/* Benefits */}
              <ul className="space-y-3 mb-6">
                {service.benefits.map((benefit, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-400">
                    <i className="ri-check-line text-copper-light mt-0.5 flex-shrink-0"></i>
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>

              {/* Link */}
              <Link
                to={service.link}
                className="flex items-center gap-2 text-sm font-medium group-hover:gap-3 transition-all cursor-pointer"
                style={{
                  background: 'linear-gradient(90deg, #A89078 0%, #C4B8A8 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Learn More
                <i className="ri-arrow-right-line text-copper-light"></i>
              </Link>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 text-white px-10 py-4 rounded-full text-base font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl"
            style={{
              background: 'linear-gradient(135deg, #7A6A5A 0%, #A89078 50%, #C4B8A8 100%)',
            }}
          >
            Request Consultation
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
